import React, { useState, useEffect } from 'react';
import { 
  Toolbar, Typography, Box, Tooltip, Paper, Button, Avatar, 
  Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, IconButton
} from '@mui/material';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import MenuIcon from '@mui/icons-material/Menu'; 
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home'; // 👈 تم استيراد أيقونة الصفحة الرئيسية
import { Link } from 'react-router-dom'; 
import { useThemeMode } from '../style/ThemeContext';

export const TopBar = () => {
  const { mode, toggleColorMode } = useThemeMode();
  const [user, setUser] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error("Failed to parse user data", error);
      }
    }
  }, []);

  const getInitials = (name) => {
    if (!name) return '';
    const nameParts = name.trim().split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`;
    }
    return nameParts[0][0];
  };

  const handleLogout = () => {
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("userToken");
    localStorage.removeItem("user");
    window.location.href = '/login'; 
  };

  const toggleMobileDrawer = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  return (
    <>
      <Paper 
    
        variant='glass' 
        elevation={0} 
        sx={{
          '@media print': { display: 'none' },
          position: 'sticky',
          top: '10px',
          mx: 2, 
          mb: 2, 
          zIndex:1
        }}
      >
        <Toolbar sx={{ minHeight: '64px', display: 'flex', justifyContent: 'space-between', px: { xs: 1, sm: 2 } }}>
          
          {/* ====== الجانب الأيمن: معلومات المستخدم ====== */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar 
              src={user?.image || ''} 
              sx={{ 
                bgcolor: 'primary.main', 
                color: 'primary.contrastText',
                width: 45, 
                height: 45,
                fontWeight: 'bold',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              {user?.fullName ? getInitials(user.fullName) : <PersonIcon />}
            </Avatar>
            
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  fontWeight: 700, 
                  color: 'text.primary',
                  lineHeight: 1.2,
                }}
              >
                {user?.fullName || 'مستخدم غير معروف'}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  fontWeight: 500,
                  mt: 0.5,
                }}
              >
                {user?.school?.schoolName || 'لم يتم تعيين مدرسة'}
              </Typography>
            </Box>
          </Box>

          {/* ====== الجانب الأيسر: الأزرار ====== */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            
            {/* زر القائمة (يظهر فقط في الشاشات الصغيرة) */}
            <IconButton
              onClick={toggleMobileDrawer}
              color="primary"
              sx={{ 
                display: { xs: 'flex', md: 'none' },
                bgcolor: 'action.hover',
                borderRadius: '10px'
              }}
            >
              <MenuIcon />
            </IconButton>

            {/* الأزرار العادية (تظهر فقط في الشاشات الكبيرة) */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
              
              {/* 👈 زر الصفحة الرئيسية الجديد للشاشات الكبيرة */}
              <Tooltip title="الصفحة الرئيسية">
                <Button
                  component={Link}
                  to="/" // عدل المسار إذا كانت صفحتك الرئيسية مسارها مختلف
                  variant='contained'
                  color='primary'
                  sx={{ minWidth: '40px', width: '40px', height: '40px', borderRadius: '10px', p: 0 }}
                >
                  <HomeIcon fontSize="small" />
                </Button>
              </Tooltip>

              <Tooltip title="قائمة الطلاب">
                <Button
                  component={Link}
                  to="/students"
                  variant='contained'
                  color='primary'
                  sx={{ minWidth: '40px', width: '40px', height: '40px', borderRadius: '10px', p: 0 }}
                >
                  <GroupIcon fontSize="small" />
                </Button>
              </Tooltip>

              <Tooltip title={mode === 'dark' ? "الوضع الفاتح" : "الوضع الداكن"}>
                <Button
                  onClick={toggleColorMode} 
                  variant='contained'
                  color='secondary'
                  sx={{ minWidth: '40px', width: '40px', height: '40px', borderRadius: '10px', p: 0 }}
                >
                  {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
                </Button>
              </Tooltip>

              <Tooltip title="تسجيل الخروج">
                <Button 
                  onClick={handleLogout} 
                  variant='contained'
                  color='error'
                  sx={{ minWidth: '40px', width: '40px', height: '40px', borderRadius: '10px', p: 0 }}
                >
                  <LogoutRoundedIcon fontSize="small" />
                </Button>
              </Tooltip>
            </Box>
          </Box>

        </Toolbar>
      </Paper>

      {/* ====== القائمة الجانبية للموبايل (Drawer) ====== */}
      <Drawer
        anchor="right"
        open={mobileDrawerOpen}
        onClose={toggleMobileDrawer}
        PaperProps={{
          sx: { width: 280, bgcolor: 'background.paper' } 
        }}
      >
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', bgcolor: 'background.default' }}>
          <IconButton 
            onClick={toggleMobileDrawer} 
            sx={{ position: 'absolute', top: 8, left: 8 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          
          <Avatar 
            src={user?.image || ''} 
            sx={{ width: 70, height: 70, mb: 2, bgcolor: 'primary.main', fontSize: '1.5rem' }}
          >
            {user?.fullName ? getInitials(user.fullName) : <PersonIcon fontSize="large" />}
          </Avatar>
          <Typography variant="subtitle1" fontWeight="bold" textAlign="center">
            {user?.fullName || 'مستخدم غير معروف'}
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {user?.school?.schoolName || 'لم يتم تعيين مدرسة'}
          </Typography>
        </Box>

        <Divider />

        <List sx={{ px: 1, py: 2 }}>
          
          {/* 👈 رابط الصفحة الرئيسية في قائمة الموبايل */}
          <ListItem disablePadding sx={{ mb: 1 }}>
            <ListItemButton 
              component={Link} 
              to="/" // عدل المسار هنا أيضاً إذا لزم الأمر
              onClick={toggleMobileDrawer}
              sx={{ borderRadius: '10px' }}
            >
              <ListItemIcon>
                <HomeIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="الصفحة الرئيسية" />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding sx={{ mb: 1 }}>
            <ListItemButton 
              component={Link} 
              to="/students" 
              onClick={toggleMobileDrawer}
              sx={{ borderRadius: '10px' }}
            >
              <ListItemIcon>
                <GroupIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="قائمة الطلاب" />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding sx={{ mb: 1 }}>
            <ListItemButton 
              onClick={() => { toggleColorMode(); toggleMobileDrawer(); }}
              sx={{ borderRadius: '10px' }}
            >
              <ListItemIcon>
                {mode === 'dark' ? <LightModeIcon color="secondary" /> : <DarkModeIcon color="secondary" />}
              </ListItemIcon>
              <ListItemText primary={mode === 'dark' ? "الوضع الفاتح" : "الوضع الداكن"} />
            </ListItemButton>
          </ListItem>

          <Divider sx={{ my: 2 }} />

          <ListItem disablePadding>
            <ListItemButton 
              onClick={handleLogout} 
              sx={{ borderRadius: '10px', '&:hover': { bgcolor: 'error.light', color: 'white' } }}
            >
              <ListItemIcon>
                <LogoutRoundedIcon color="error" />
              </ListItemIcon>
              <ListItemText primary="تسجيل الخروج" sx={{ color: 'error.main' }} />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
    </>
  );
};

export default TopBar;